import React from 'react';
import styled, { css } from 'styled-components';
import {
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
} from '../styles';
import { Container } from './ui';
import EventCard from './EventCard';
import ListingCtaCard from './ListingCtaCard';
import Pagination from './Pagination';

const StyledEventListing = styled.section`
  margin-bottom: 40px;

  ${minBreakpointQuery.small`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 60px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 70px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-bottom: 80px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 100px;
  `}
`;

const StyledContainer = styled(Container)`
  ${({ isVenuePage }) => {
    if (isVenuePage) {
      return css`
        padding-left: 0;
        padding-right: 0;
      `;
    }
  }}
`;

const StyledEvents = styled.div`
  margin-top: 30px;

  ${minBreakpointQuery.medium`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-top: 60px;
  `}
`;

const StyledHeading = styled.h2`
  display: flex;
  align-items: center;
  ${fontSize(20)};
  font-weight: ${fontWeights.regular};

  ${minBreakpointQuery.mlarge`
    ${fontSize(25)};
  `}

  span {
    flex-shrink: 0;
    margin-left: 20px;
  }

  &:before {
    content: '';
    height: 1px;
    width: 100%;
    background-color: ${standardColours.grey};
  }
`;

const StyledItems = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 20px;

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${({ isVenuePage }) => {
    if (!isVenuePage) {
      return css`
        ${minBreakpointQuery.smedium`
          margin-top: 30px;
          gap: 15px;
        `}

        ${minBreakpointQuery.medium`
          margin-top: 40px;
        `}

        ${minBreakpointQuery.mlarge`
          grid-template-columns: repeat(3, 1fr);
        `}

        ${minBreakpointQuery.large`
          margin-top: 50px;
          gap: 20px;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.smedium`
          grid-template-columns: 1fr;
        `}

        ${minBreakpointQuery.medium`
          margin-top: 30px;
          grid-template-columns: repeat(2, 1fr);
        `}

        ${minBreakpointQuery.large`
          margin-top: 40px;
        `}

        ${minBreakpointQuery.xlarge`
          margin-top: 50px;
          gap: 20px;
        `}
      `;
    }
  }}
`;

const StypedPagination = styled(Pagination)`
  margin-top: 30px;

  ${minBreakpointQuery.small`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
  `}
`;

const EventListing = ({ items, filterResults, meta, isVenuePage }) => {
  // Group events by date
  const dateGroups = items.reduce((acc, obj) => {
    const key = obj.monthYear;
    const curGroup = acc[key] ?? [];
    return { ...acc, [key]: [...curGroup, obj] };
  }, {});

  // Filter out groups that have only CTAs
  const filteredDateGroups = Object.fromEntries(
    Object.entries(dateGroups).filter(
      ([, events]) => events.some(event => !event.listingCta), // Keep groups that have at least one non-CTA event
    ),
  );

  if (Object.keys(filteredDateGroups).length > 0) {
    return (
      <StyledEventListing>
        <StyledContainer isVenuePage={isVenuePage}>
          {Object.keys(filteredDateGroups).map((dateGroup, id) => (
            <StyledEvents key={id}>
              <StyledHeading {...(isVenuePage && { as: 'h3' })}>
                <span>{dateGroup}</span>
              </StyledHeading>
              <StyledItems isVenuePage={isVenuePage}>
                {filteredDateGroups[dateGroup].map((item, id) =>
                  item.listingCta ? (
                    <ListingCtaCard
                      key={`cta-${id}`}
                      {...item.listingCta}
                      isVenuePage={isVenuePage}
                    />
                  ) : (
                    <EventCard key={id} {...item} isVenuePage={isVenuePage} />
                  ),
                )}
              </StyledItems>
            </StyledEvents>
          ))}
          {filterResults === undefined && <StypedPagination {...meta} />}
        </StyledContainer>
      </StyledEventListing>
    );
  }
};

export default EventListing;
